import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const ResetPasswordPage = () => {
  const { token: paramToken } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(true);
  const [token, setToken] = useState(null);

  // HashRouter focused token extraction
  const extractTokenFromURL = () => {
    const fullURL = window.location.href;
    const hash = location.hash || window.location.hash;
    const pathname = location.pathname;
    const search = location.search;


    // Priority 1: useParams token (works with both routers)
    if (paramToken && paramToken.trim()) {
      // console.log('✅ Token from useParams:', paramToken);
      return paramToken.trim();
    }

    // Priority 2: HashRouter - Extract from hash
    if (hash) {
      // Format: #/reset-password/c522b159fc864a631463c276bbc58aab28d42dd2b0ffe0cf0802e5b46ce4080e
      const hashPathPattern = /#\/reset-password\/([a-zA-Z0-9\-_\.]{8,})/;
      const hashMatch = hash.match(hashPathPattern);
      if (hashMatch && hashMatch[1]) {
        // console.log('✅ Token from HashRouter path:', hashMatch[1]);
        return hashMatch[1].trim();
      }

      // Format: #/reset-password?token=c522b159fc864a631463c276bbc58aab28d42dd2b0ffe0cf0802e5b46ce4080e
      const hashQueryPattern = /#\/reset-password\?(.+)/;
      const hashQueryMatch = hash.match(hashQueryPattern);
      if (hashQueryMatch && hashQueryMatch[1]) {
        const hashParams = new URLSearchParams(hashQueryMatch[1]);
        const hashToken = hashParams.get('token');
        if (hashToken && hashToken.trim()) {
          // console.log('✅ Token from HashRouter query:', hashToken);
          return hashToken.trim();
        }
      }

      // Format: Direct hash #c522b159fc864a631463c276bbc58aab28d42dd2b0ffe0cf0802e5b46ce4080e
      const directHashPattern = /^#([a-zA-Z0-9\-_\.]{32,})$/;
      const directMatch = hash.match(directHashPattern);
      if (directMatch && directMatch[1]) {
        // console.log('✅ Token from direct hash:', directMatch[1]);
        return directMatch[1].trim();
      }
    }

    // Priority 3: BrowserRouter - Extract from pathname
    if (pathname) {
      const pathPattern = /\/reset-password\/([a-zA-Z0-9\-_\.]{8,})/;
      const pathMatch = pathname.match(pathPattern);
      if (pathMatch && pathMatch[1]) {
        // console.log('✅ Token from BrowserRouter path:', pathMatch[1]);
        return pathMatch[1].trim();
      }
    }

    // Priority 4: Query parameters
    if (search) {
      const urlParams = new URLSearchParams(search);
      const queryToken = urlParams.get('token');
      if (queryToken && queryToken.trim()) {
        // console.log('✅ Token from query params:', queryToken);
        return queryToken.trim();
      }
    }

    // Priority 5: Full URL fallback parsing
    const urlTokenPatterns = [
      // HashRouter patterns
      /#\/reset-password\/([a-zA-Z0-9\-_\.]{8,})/,
      /reset-password\/([a-zA-Z0-9\-_\.]{8,})/,
      /token=([a-zA-Z0-9\-_\.]{8,})/,
      /#([a-zA-Z0-9\-_\.]{32,})$/
    ];

    for (let i = 0; i < urlTokenPatterns.length; i++) {
      const pattern = urlTokenPatterns[i];
      const match = fullURL.match(pattern);
      if (match && match[1]) {
        // console.log(`✅ Token found via pattern ${i + 1}:`, match[1]);
        return match[1].trim();
      }
    }

    // console.log('❌ No token detected in URL');
    return null;
  };

  // API service class
  class AuthService {
    constructor() {
      this.baseURL = (import.meta.env.VITE_API_URL || 'http://localhost:5000') + '/api';
    }

    async request(endpoint, options = {}) {
      const url = `${this.baseURL}${endpoint}`;
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      };

      try {
        const response = await fetch(url, config);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.message || 'Something went wrong');
        }

        return data;
      } catch (error) {
        console.error('API Error:', error);
        throw error;
      }
    }

    async resetPassword(token, password, confirmPassword) {
      return this.request(`/auth/reset-password/${token}`, {
        method: 'PUT',
        body: JSON.stringify({ password, confirmPassword }),
      });
    }

    // Optional: Method to validate token before form submission
    async validateResetToken(token) {
      return this.request(`/auth/validate-reset-token/${token}`, {
        method: 'GET',
      });
    }
  }

  const authService = new AuthService();

  // Extract and validate token - optimized for HashRouter
  useEffect(() => {
    // console.log('🚀 HashRouter useEffect triggered');
    const extractedToken = extractTokenFromURL();
    
    if (!extractedToken) {
      // console.log('❌ No token found, setting invalid state');
      setTokenValid(false);
      setErrors({ 
        submit: 'Token reset password tidak ditemukan. Silakan coba lagi dari email yang Anda terima.' 
      });
      return;
    }

    // console.log('✅ Token extracted successfully:', extractedToken.substring(0, 10) + '...');
    setToken(extractedToken);
    setTokenValid(true);
    
    // Clear any previous errors
    setErrors({});

    // Optional: Validate token with backend
    const validateToken = async () => {
      try {
        // Uncomment this if you have token validation endpoint
        // await authService.validateResetToken(extractedToken);
        // console.log('✅ Token validation would succeed');
        setTokenValid(true);
      } catch (error) {
        console.error('❌ Token validation failed:', error);
        setTokenValid(false);
        setErrors({ 
          submit: 'Link reset password tidak valid atau sudah kedaluwarsa. Silakan minta link reset password baru.' 
        });
      }
    };

    // validateToken(); // Uncomment if you want to validate token on load
  }, [
    paramToken, 
    location.pathname, 
    location.hash, 
    location.search,
    // Add window.location for HashRouter changes
    window.location.href
  ]);

  // HashRouter specific: Listen for hash changes
  useEffect(() => {
    const handleHashChange = (event) => {
      // console.log('🔄 Hash changed from:', event.oldURL, 'to:', event.newURL);
      
      // Small delay to ensure location is updated
      setTimeout(() => {
        const extractedToken = extractTokenFromURL();
        if (extractedToken && extractedToken !== token) {
          // console.log('🔄 Token updated via hash change:', extractedToken.substring(0, 10) + '...');
          setToken(extractedToken);
          setTokenValid(true);
          setErrors({});
        }
      }, 50);
    };

    // Listen for both hash changes and popstate
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
    };
  }, [token]);

  // Additional effect to handle direct URL access with HashRouter
  useEffect(() => {
    // Handle case where user directly accesses HashRouter URL
    if (!token && window.location.hash.includes('reset-password')) {
      // console.log('🔄 Direct HashRouter access detected, extracting token...');
      setTimeout(() => {
        const extractedToken = extractTokenFromURL();
        if (extractedToken) {
          setToken(extractedToken);
          setTokenValid(true);
          setErrors({});
        }
      }, 100);
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear general errors
    if (errors.submit) {
      setErrors(prev => ({
        ...prev,
        submit: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.password.trim()) {
      newErrors.password = 'Password baru wajib diisi';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password minimal 6 karakter';
    }
    
    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Konfirmasi password wajib diisi';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Password dan konfirmasi password tidak sama';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || !token) return;
    
    setIsLoading(true);
    setErrors({});
    
    try {
      const response = await authService.resetPassword(
        token,
        formData.password,
        formData.confirmPassword
      );
      
      if (response.success) {
        setIsSuccess(true);
        // Auto redirect to login after 5 seconds
        setTimeout(() => {
          navigate('/login');
        }, 5000);
      }
      
    } catch (error) {
      console.error('Reset password failed:', error);
      
      if (error.message?.includes('tidak valid') || error.message?.includes('kedaluwarsa')) {
        setTokenValid(false);
        setErrors({ 
          submit: 'Link reset password tidak valid atau sudah kedaluwarsa. Silakan minta link reset password baru.' 
        });
      } else if (error.message?.includes('tidak sama')) {
        setErrors({ 
          confirmPassword: 'Password dan konfirmasi password tidak sama' 
        });
      } else {
        setErrors({ 
          submit: error.message || 'Terjadi kesalahan saat reset password. Silakan coba lagi.' 
        });
      }
    }
    
    setIsLoading(false);
  };

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="text-center mb-8">
            <img
              src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
              alt="Telnet Indonesia"
              className="w-42 h-32 object-contain mx-auto"
              style={{ maxWidth: '70%' }}
            />
          </div>
          
          <div className="card-gradient rounded-3xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 success-gradient rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Password Berhasil Direset!</h2>
            <p className="text-gray-300 mb-6">
              Password Anda telah berhasil direset. Anda sekarang dapat login dengan password baru Anda.
            </p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="block w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 text-center"
              >
                Lanjut ke Login
              </Link>
              <Link
                to="/"
                className="block w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-300 text-center"
              >
                Kembali ke Beranda
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 flex items-center justify-center">
        <div className="max-w-md mx-auto px-4">
          <div className="text-center mb-8">
            <img
              src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
              alt="Telnet Indonesia"
              className="w-42 h-32 object-contain mx-auto"
              style={{ maxWidth: '70%' }}
            />
          </div>
          
          <div className="card-gradient rounded-3xl p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-6 error-gradient rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Link Tidak Valid</h2>
            <p className="text-gray-300 mb-6">{errors.submit}</p>
            
            {/* Debug info - Remove in production */}
            {import.meta.env.NODE_ENV === 'development' && (
              <div className="mb-6 text-left">
                <div className="bg-gray-800 text-gray-300 p-3 rounded text-xs">
                  <p><strong>Debug Info:</strong></p>
                  <p>Pathname: {location.pathname}</p>
                  <p>Hash: {location.hash}</p>
                  <p>Search: {location.search}</p>
                  <p>Full URL: {window.location.href}</p>
                  <p>Detected Token: {token || 'none'}</p>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <Link
                to="/forgot-password"
                className="block w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 text-center"
              >
                Minta Link Baru
              </Link>
              <Link
                to="/login"
                className="block w-full px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all duration-300 text-center"
              >
                Kembali ke Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <style>
        {` 
         .card-gradient {
            background: #ffffffcc;
            border: 1px solid rgba(30, 155, 76, 0.3);
            backdrop-filter: blur(10px);
          }
          
          .input-field {
            background: rgba(255, 255, 255, 0.7);
            border: 1px solid rgba(30, 155, 76, 0.4);
            transition: all 0.3s ease;
          }
          
          .input-field:focus {
            border-color: rgba(30, 155, 76, 0.7);
            box-shadow: 0 0 0 3px rgba(30, 155, 76, 0.2);
            background: #fff;
          }
          
          .btn-gradient {
            background: linear-gradient(135deg, #1E9B4C, #136d35);
          }
          
          .btn-gradient:hover {
            background: linear-gradient(135deg, #136d35, #1E9B4C);
            transform: translateY(-2px);
            box-shadow: 0 10px 25px rgba(30, 155, 76, 0.3);
          }

          .success-gradient {
            background: linear-gradient(135deg, #059669, #047857);
          }
          
          .error-gradient {
            background: linear-gradient(135deg, #dc2626, #b91c1c);
          }
        `}
      </style>
      
      {/* Hero Section */}
      <section className="hero-gradient py-20"
      style={{
        backgroundImage: "url('https://telnet.net.id/assets/bg.png')",
        backgroundColor:  '#dfe4e0ff',
        backgroundBlendMode: 'overlay',
      }}
      >
        <div className="max-w-md mx-auto px-4">
          <div className="text-center mb-8">
            <img
                src="https://telnet.co.id/wp-content/uploads/2024/09/TELNET-INDONESIA-3.png"
                alt="Icon Image"
                className="w-42 h-32 object-contain mx-auto"
                style={{ maxWidth: '70%' }}
            />
            <h1 className="text-4xl font-bold text-black mb-4">
              Password Baru
            </h1>
            <p className="text-xl text-black">
              Masukkan password baru Anda
            </p>
          </div>

          {/* Reset Password Form */}
          <div className="card-gradient rounded-3xl p-8 shadow-xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Messages */}
              {errors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-red-600 text-sm">{errors.submit}</p>
                </div>
              )}

              {/* Password Field */}
              <div className="space-y-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password Baru
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="input-field w-full px-4 py-3 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none pr-12"
                    placeholder="Masukkan password baru"
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 hover:text-[#1E9B4C] transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-600 text-sm">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div className="space-y-2">
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  Konfirmasi Password Baru
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="input-field w-full px-4 py-3 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none pr-12"
                    placeholder="Konfirmasi password baru"
                    disabled={isLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600 hover:text-[#1E9B4C] transition-colors"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-600 text-sm">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-blue-700 text-sm font-medium">Persyaratan Password</p>
                    <ul className="text-blue-600 text-sm mt-1 list-disc list-inside space-y-1">
                      <li>Minimal 6 karakter</li>
                      <li>Gunakan kombinasi huruf dan angka</li>
                      <li>Hindari menggunakan informasi pribadi</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || !token}
                className="w-full px-6 py-3 btn-gradient text-white font-semibold rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Menyimpan password..." : "Reset Password"}
              </button>

              {/* Back to Login */}
              <div className="text-center">
                <Link
                  to="/login"
                  className="text-[#1E9B4C] hover:underline font-semibold transition-colors inline-flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span>Kembali ke Login</span>
                </Link>
              </div>
            </form>
          </div>

          {/* Security Notice */}
          <div className="mt-8">
            <div className="card-gradient rounded-2xl p-6">
              <div className="flex items-start space-x-3">
                <svg className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Keamanan Akun</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Setelah reset password berhasil, kami sarankan untuk logout dari semua perangkat lain dan login kembali menggunakan password baru Anda untuk memastikan keamanan akun.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResetPasswordPage;